import axios from 'axios';

export const PAYMENT_TYPE_CATEGORIES = Object.freeze([
  { label: 'Cash', value: 'cash' },
  { label: 'Card', value: 'card' },
  { label: 'Check', value: 'check' },
  { label: 'Bank transfer', value: 'bank_transfer' },
  { label: 'Financing', value: 'financing' },
  { label: 'Store credit', value: 'store_credit' },
  { label: 'Other', value: 'other' },
]);

export const PAYMENT_TYPE_USAGES = Object.freeze([
  { label: 'Repair Order', value: 'repair_order' },
  { label: 'Purchase Order', value: 'purchase_order' },
]);

const categoryValues = new Set(PAYMENT_TYPE_CATEGORIES.map((category) => category.value));
const usageValues = new Set(PAYMENT_TYPE_USAGES.map((usage) => usage.value));
const resolveShopId = (shopId) => Number(shopId || 0) || 0;

const normalizeUsageTypes = (types) => Array.from(new Set(
  (Array.isArray(types) ? types : []).filter((type) => usageValues.has(type)),
));

const normalizeShopIds = (shopIds, fallbackShopId = null) => {
  const normalizedShopIds = Array.from(new Set((Array.isArray(shopIds) ? shopIds : [])
    .map((shopId) => Number(shopId))
    .filter((shopId) => Number.isFinite(shopId) && shopId > 0)))
    .sort((left, right) => left - right);

  if (normalizedShopIds.length) {
    return normalizedShopIds;
  }

  const fallback = resolveShopId(fallbackShopId);
  return fallback ? [fallback] : [];
};

const normalizePaymentTypePayload = (payload) => ({
  name: String(payload.name || '').trim(),
  category: categoryValues.has(payload.category) ? payload.category : String(payload.category || ''),
  usageTypes: normalizeUsageTypes(payload.usageTypes),
  isActive: Boolean(payload.isActive),
  scope: payload.scope === 'all-shops' ? 'all-shops' : 'shop',
  shopIds: payload.scope === 'all-shops'
    ? normalizeShopIds(payload.shopIds)
    : normalizeShopIds(payload.shopIds, payload.shopId),
  shopId: resolveShopId(payload.shopId),
  accessibleShopIds: normalizeShopIds(payload.accessibleShopIds),
});

const normalizePaymentTypeResponse = (paymentType = {}, fallbackShopId = null) => ({
  id: paymentType.id,
  name: String(paymentType.name || ''),
  category: categoryValues.has(paymentType.category) ? paymentType.category : String(paymentType.category || ''),
  usageTypes: normalizeUsageTypes(paymentType.usageTypes),
  isActive: Boolean(paymentType.isActive),
  used: Boolean(paymentType.used),
  shopIds: normalizeShopIds(paymentType.shopIds, fallbackShopId),
  scope: String(paymentType.scope || 'shop'),
  shopStatuses: Array.isArray(paymentType.shopStatuses)
    ? paymentType.shopStatuses.map((status) => ({
      shopId: resolveShopId(status.shopId),
      active: Boolean(status.active),
    })).filter((status) => status.shopId > 0)
    : [],
  createdAt: paymentType.createdAt || null,
  updatedAt: paymentType.updatedAt || null,
});

const normalizePaymentTypeError = (error, fallbackMessage) => {
  const normalizedError = new Error(
    error?.response?.data?.message
      || error?.message
      || fallbackMessage,
  );
  normalizedError.status = error?.response?.status || error?.status || null;
  normalizedError.errors = error?.response?.data?.errors || error?.errors || {};

  return normalizedError;
};

const DEFAULT_META = Object.freeze({
  page: 1,
  per_page: 15,
  total: 0,
  has_more: false,
});

export const paymentTypeClient = Object.freeze({
  async listPaymentTypes({
    shopId,
    scope = null,
    active = null,
    page = 1,
    perPage = DEFAULT_META.per_page,
  }) {
    const normalizedShopId = resolveShopId(shopId);

    try {
      const response = await axios.get(route('shop-management.payment-types.data', {
        shop_id: normalizedShopId,
      }), {
        params: {
          ...(scope ? { scope } : {}),
          ...(active === null || active === undefined ? {} : { active }),
          page,
          per_page: perPage,
        },
      });

      return {
        rows: Array.isArray(response?.data?.rows)
          ? response.data.rows.map((paymentType) => normalizePaymentTypeResponse(paymentType, normalizedShopId))
          : [],
        meta: {
          ...DEFAULT_META,
          ...(response?.data?.meta || {}),
        },
        paymentCategories: Array.isArray(response?.data?.paymentCategories)
          ? response.data.paymentCategories
          : PAYMENT_TYPE_CATEGORIES,
      };
    } catch (error) {
      throw normalizePaymentTypeError(error, 'Unable to load payment types.');
    }
  },

  async createPaymentType(payload) {
    const normalizedPayload = normalizePaymentTypePayload(payload);

    try {
      const response = await axios.post(route('shop-management.payment-types.store', {
        shop_id: normalizedPayload.shopId,
      }), normalizedPayload);

      return normalizePaymentTypeResponse(response?.data?.paymentType, normalizedPayload.shopId);
    } catch (error) {
      throw normalizePaymentTypeError(error, 'Unable to create payment type.');
    }
  },

  async updatePaymentType(id, payload) {
    const normalizedPayload = normalizePaymentTypePayload(payload);

    try {
      const response = await axios.patch(route('shop-management.payment-types.update', {
        shop_id: normalizedPayload.shopId,
        paymentType: id,
      }), normalizedPayload);

      return normalizePaymentTypeResponse(response?.data?.paymentType, normalizedPayload.shopId);
    } catch (error) {
      throw normalizePaymentTypeError(error, 'Unable to update payment type.');
    }
  },

  async deletePaymentType(id, {
    shopId,
    scope = null,
    operationScope = null,
    shopIds = [],
  } = {}) {
    try {
      await axios.delete(route('shop-management.payment-types.destroy', {
        shop_id: resolveShopId(shopId),
        paymentType: id,
      }), {
        params: {
          ...(scope ? { scope } : {}),
          ...(operationScope ? { operationScope } : {}),
          ...(Array.isArray(shopIds) && shopIds.length ? { shopIds: normalizeShopIds(shopIds) } : {}),
        },
      });
    } catch (error) {
      throw normalizePaymentTypeError(error, 'Unable to delete payment type.');
    }
  },
});
